import { useToast } from "@/components/ui/toast"
import { getToken } from "@/utils/functions/getToken"
import {
  ServiceAddBeautyInput,
  ServiceAddBeautyOutput,
  ServiceDeleteBeautyInput,
  ServiceDeleteBeautyOutput,
  ServiceGetBeautyOutput,
  ServiceUpdateBeautyInput,
  ServiceUpdateBeautyOutput,
} from "@okeys/shared-utils"
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query"
import { fetchUserAttributes } from "aws-amplify/auth"
import { customAxios } from "../utils/customAxios/customAxios"

export const useServicesQuery = () => {
  const queryOptions: UseQueryOptions<
    ServiceGetBeautyOutput,
    Error,
    ServiceGetBeautyOutput,
    string[]
  > = {
    queryKey: [`services`],
    queryFn: async () => {
      const token = await getToken()
      const userAttributes = await fetchUserAttributes()
      const shopId = userAttributes["custom:shopId"]
      if (!token) {
        throw new Error("No token")
      }
      const dataReturn = await customAxios.get(`/service?shopId=${shopId}`, {
        headers: {
          Authorization: token,
        },
      })
      return dataReturn.data as ServiceGetBeautyOutput
    },
    staleTime: 300000,
    refetchOnWindowFocus: false,
  }

  const { isPending, isError, data, isSuccess, refetch } =
    useQuery(queryOptions)

  return {
    pendingServices: isPending,
    errorServices: isError,
    successServices: isSuccess,
    refetchServices: refetch,
    services: data,
  }
}

export const useAddServiceMutation = () => {
  const queryClient = useQueryClient()
  const { addToast } = useToast()

  return useMutation({
    mutationFn: async (
      serviceData: ServiceAddBeautyInput
    ): Promise<ServiceAddBeautyOutput> => {
      const token = await getToken()
      if (!token) {
        throw new Error("No token")
      }

      const response = await customAxios.post("/service", serviceData, {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
      })

      return response.data as ServiceAddBeautyOutput
    },
    // Optimistic update : mise à jour immédiate de l'UI
    onMutate: async (newService: ServiceAddBeautyInput) => {
      // Annuler les requêtes en cours pour éviter les conflits
      await queryClient.cancelQueries({ queryKey: ["services"] })

      // Sauvegarder l'état précédent pour le rollback
      const previousServices = queryClient.getQueryData<ServiceGetBeautyOutput>(
        ["services"]
      )

      // Créer un service temporaire avec un ID fictif
      const tempService = {
        ...newService,
        id: `temp-${Date.now()}`, // ID temporaire
      } as ServiceGetBeautyOutput[number]

      // Mettre à jour optimistiquement le cache
      queryClient.setQueryData<ServiceGetBeautyOutput>(["services"], (old) => {
        return old ? [...old, tempService] : [tempService]
      })

      // Retourner le contexte pour le rollback
      return { previousServices }
    },
    onSuccess: () => {
      addToast({
        type: "success",
        title: "Service ajouté",
        description: "Le service a été créé avec succès",
      })
      // Invalider et refetch pour avoir les vraies données du serveur
      queryClient.invalidateQueries({ queryKey: ["services"] })
    },
    onError: (error: Error, _variables: ServiceAddBeautyInput, context) => {
      console.error("Erreur lors de l'ajout du service:", error)
      // Rollback : restaurer l'état précédent
      if (context?.previousServices) {
        queryClient.setQueryData(["services"], context.previousServices)
      }

      addToast({
        type: "error",
        title: "Erreur lors de l'ajout",
        description: "Impossible d'ajouter le service",
        action: {
          label: "Réessayer",
          onClick: () => {
            // La mutation sera relancée depuis le composant
          },
        },
      })
    },
    onSettled: () => {
      // Toujours invalider les queries à la fin (succès ou erreur)
      queryClient.invalidateQueries({ queryKey: ["services"] })
    },
  })
}

export const useDeleteServiceMutation = () => {
  const queryClient = useQueryClient()
  const { addToast } = useToast()

  return useMutation({
    mutationFn: async (
      serviceDeleteData: ServiceDeleteBeautyInput
    ): Promise<ServiceDeleteBeautyOutput> => {
      const token = await getToken()
      if (!token) {
        throw new Error("No token")
      }

      const response = await customAxios.delete("/service", {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
        data: serviceDeleteData,
      })

      return response.data as ServiceDeleteBeautyOutput
    },
    // Optimistic update : suppression immédiate de l'UI
    onMutate: async (deleteData: ServiceDeleteBeautyInput) => {
      // Annuler les requêtes en cours pour éviter les conflits
      await queryClient.cancelQueries({ queryKey: ["services"] })

      // Sauvegarder l'état précédent pour le rollback
      const previousServices = queryClient.getQueryData<ServiceGetBeautyOutput>(
        ["services"]
      )

      // Supprimer optimistiquement le service du cache
      queryClient.setQueryData<ServiceGetBeautyOutput>(["services"], (old) => {
        return old
          ? old.filter((service) => service.id !== deleteData.serviceId)
          : []
      })

      // Retourner le contexte pour le rollback
      return { previousServices }
    },
    onSuccess: () => {
      addToast({
        type: "success",
        title: "Service supprimé",
        description: "Le service a été supprimé avec succès",
      })
      // Invalider et refetch pour synchroniser avec le serveur
      queryClient.invalidateQueries({ queryKey: ["services"] })
    },
    onError: (error: Error, _variables: ServiceDeleteBeautyInput, context) => {
      console.error("Erreur lors de la suppression du service:", error)
      // Rollback : restaurer l'état précédent
      if (context?.previousServices) {
        queryClient.setQueryData(["services"], context.previousServices)
      }

      addToast({
        type: "error",
        title: "Erreur lors de la suppression",
        description: "Impossible de supprimer le service",
        action: {
          label: "Réessayer",
          onClick: () => {
            // La mutation sera relancée depuis le composant
          },
        },
      })
    },
    onSettled: () => {
      // Toujours invalider les queries à la fin (succès ou erreur)
      queryClient.invalidateQueries({ queryKey: ["services"] })
    },
  })
}

export const useUpdateServiceMutation = () => {
  const queryClient = useQueryClient()
  const { addToast } = useToast()

  return useMutation({
    mutationFn: async (
      serviceUpdateData: ServiceUpdateBeautyInput
    ): Promise<ServiceUpdateBeautyOutput> => {
      const token = await getToken()
      if (!token) {
        throw new Error("No token")
      }

      const response = await customAxios.put("/service", serviceUpdateData, {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
      })

      return response.data as ServiceUpdateBeautyOutput
    },
    // Optimistic update : mise à jour immédiate de l'UI
    onMutate: async (updateData: ServiceUpdateBeautyInput) => {
      // Annuler les requêtes en cours pour éviter les conflits
      await queryClient.cancelQueries({ queryKey: ["services"] })

      // Sauvegarder l'état précédent pour le rollback
      const previousServices = queryClient.getQueryData<ServiceGetBeautyOutput>(
        ["services"]
      )

      // Mettre à jour optimistiquement le service dans le cache
      queryClient.setQueryData<ServiceGetBeautyOutput>(["services"], (old) => {
        return old
          ? old.map((service) =>
              service.id === updateData.serviceId
                ? ({
                    ...service,
                    ...updateData,
                  } as ServiceGetBeautyOutput[number])
                : service
            )
          : []
      })

      // Retourner le contexte pour le rollback
      return { previousServices }
    },
    onSuccess: () => {
      addToast({
        type: "success",
        title: "Service modifié",
        description: "Le service a été mis à jour avec succès",
      })
      // Invalider et refetch pour synchroniser avec le serveur
      queryClient.invalidateQueries({ queryKey: ["services"] })
    },
    onError: (error: Error, _variables: ServiceUpdateBeautyInput, context) => {
      console.error("Erreur lors de la mise à jour du service:", error)
      // Rollback : restaurer l'état précédent
      if (context?.previousServices) {
        queryClient.setQueryData(["services"], context.previousServices)
      }

      addToast({
        type: "error",
        title: "Erreur lors de la modification",
        description: "Impossible de modifier le service",
        action: {
          label: "Réessayer",
          onClick: () => {
            // La mutation sera relancée depuis le composant
          },
        },
      })
    },
    onSettled: () => {
      // Toujours invalider les queries à la fin (succès ou erreur)
      queryClient.invalidateQueries({ queryKey: ["services"] })
    },
  })
}
